<template>
  <div class="drp-page">
    <header class="drp-header">
      <button type="button" class="drp-back" @click="goBack">‹ Administration</button>
      <h1 class="drp-title">Directive Re-authorization Rules</h1>
      <p class="drp-lede">
        Some states require a Directive Record to be re-authorized by an NPI holder on a fixed
        cadence. List only the states that impose such a rule, and how long each state's
        authorization lasts.
      </p>
    </header>

    <div v-if="!canManage" class="drp-denied">
      You do not have permission to manage directive re-authorization rules.
    </div>

    <template v-else>
      <p v-if="error" class="drp-error" role="alert">{{ error }}</p>

      <section class="drp-card">
        <h2 class="drp-card-title">{{ editing ? "Edit rule" : "Add a state" }}</h2>
        <form class="drp-form" @submit.prevent="save">
          <label class="drp-field">
            <span class="drp-label">State</span>
            <select v-model="form.stateCode" class="drp-input" :disabled="editing || saving">
              <option value="">Select a state…</option>
              <option v-for="s in selectableStates" :key="s.code" :value="s.code">
                {{ s.name }} ({{ s.code }})
              </option>
            </select>
          </label>

          <label class="drp-field">
            <span class="drp-label">Re-authorization required every</span>
            <span class="drp-days">
              <input
                v-model.number="form.reauthDays"
                class="drp-input drp-input-days"
                type="number"
                :min="REAUTH_DAYS_MIN"
                :max="REAUTH_DAYS_MAX"
                :disabled="saving"
              />
              <span class="drp-unit">days</span>
            </span>
          </label>

          <div class="drp-actions">
            <button type="submit" class="drp-btn drp-btn-primary" :disabled="saving || !canSubmit">
              {{ saving ? "Saving…" : editing ? "Save changes" : "Add rule" }}
            </button>
            <button v-if="editing" type="button" class="drp-btn" :disabled="saving" @click="resetForm">
              Cancel
            </button>
          </div>
        </form>
      </section>

      <section class="drp-card">
        <h2 class="drp-card-title">States requiring re-authorization</h2>

        <p v-if="loading" class="drp-muted">Loading…</p>

        <!--
          An empty table is a meaningful answer, not a failure: it means no state
          this organization operates in imposes a re-authorization rule. Say so,
          so nobody reads it as "not loaded yet" or "broken".
        -->
        <p v-else-if="!policies.length" class="drp-empty">
          No states are listed. <strong>No directive currently requires re-authorization.</strong>
          States that are not listed here impose no re-authorization requirement — add a state
          above to start enforcing one.
        </p>

        <table v-else class="drp-table">
          <thead>
            <tr>
              <th scope="col">State</th>
              <th scope="col">Re-authorize every</th>
              <th scope="col">Last changed by</th>
              <th scope="col"><span class="drp-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in policies" :key="p.state_code">
              <td>{{ stateNameForCode(p.state_code) }} <span class="drp-code">{{ p.state_code }}</span></td>
              <td>{{ p.reauth_days }} days</td>
              <td class="drp-muted">{{ p.updated_by_name || "—" }}</td>
              <td class="drp-row-actions">
                <button type="button" class="drp-btn drp-btn-sm" :disabled="saving" @click="edit(p)">
                  Edit
                </button>
                <button
                  type="button"
                  class="drp-btn drp-btn-sm drp-btn-danger"
                  :disabled="saving"
                  @click="remove(p)"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import { PRIVILEGES } from "@/constants/privileges";
import { US_STATE_OPTIONS, stateNameForCode } from "@/constants/US-states";
import {
  getDirectiveReauthPolicies,
  getDirectiveReauthPolicyImpact,
  saveDirectiveReauthPolicy,
  deleteDirectiveReauthPolicy,
  REAUTH_DAYS_MIN,
  REAUTH_DAYS_MAX,
} from "@/api/directiveReauthPolicyApi";

export default {
  name: "DirectiveReauthPolicyPage",
  data() {
    return {
      policies: [],
      loading: false,
      saving: false,
      error: "",
      editing: false,
      form: { stateCode: "", reauthDays: 90 },
      REAUTH_DAYS_MIN,
      REAUTH_DAYS_MAX,
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    sessionActor() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile);
    },
    accountnumber() {
      return this.sessionActor.accountnumber;
    },
    currentUserId() {
      return this.sessionActor.unique_ID;
    },
    currentUserName() {
      return this.sessionActor.displayName || "Unknown user";
    },
    privilegesStore() {
      return usePrivilegesStore();
    },
    canManage() {
      return this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_POLICY_MANAGE);
    },
    /** Editing pins the state; adding hides states that already have a rule. */
    selectableStates() {
      if (this.editing) return US_STATE_OPTIONS;
      const taken = new Set(this.policies.map((p) => p.state_code));
      return US_STATE_OPTIONS.filter((s) => !taken.has(s.code));
    },
    canSubmit() {
      const d = Number(this.form.reauthDays);
      return (
        !!this.form.stateCode &&
        Number.isFinite(d) &&
        d >= REAUTH_DAYS_MIN &&
        d <= REAUTH_DAYS_MAX
      );
    },
  },
  async mounted() {
    if (this.canManage) await this.load();
  },
  methods: {
    stateNameForCode,
    goBack() {
      this.$router.push("/administration");
    },
    resetForm() {
      this.editing = false;
      this.form = { stateCode: "", reauthDays: 90 };
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await getDirectiveReauthPolicies({
          accountnumber: this.accountnumber,
          actorId: this.currentUserId,
        });
        if (res && res.status === "Successful") {
          this.policies = Array.isArray(res.data) ? res.data : [];
        } else {
          this.error = (res && res.message) || "Could not load re-authorization rules.";
        }
      } catch (e) {
        console.error("[directive-policy] load failed", e);
        this.error = "Could not load re-authorization rules.";
      } finally {
        this.loading = false;
      }
    },
    edit(policy) {
      this.editing = true;
      this.form = { stateCode: policy.state_code, reauthDays: Number(policy.reauth_days) };
    },
    /**
     * Counts of the records a save/delete will touch. Returns null when the
     * lookup fails — the caller falls back to a confirmation without numbers
     * rather than blocking the action on a count query.
     */
    async impactFor(stateCode) {
      try {
        const res = await getDirectiveReauthPolicyImpact({
          accountnumber: this.accountnumber,
          actorId: this.currentUserId,
          stateCode,
        });
        if (res && res.status === "Successful" && res.data) {
          return { awaiting: Number(res.data.awaiting) || 0, holding: Number(res.data.holding) || 0 };
        }
      } catch (e) {
        console.error("[directive-policy] impact lookup failed", e);
      }
      return null;
    },
    async save() {
      if (!this.canSubmit) return;

      /* Saving applies the rule to records signed before it existed, so this
         writes to clinical records — the admin sees the count first. The two
         numbers say different things and both matter: records with no deadline
         GET one dated from today, while records already carrying one keep it
         until their next re-authorization. Without that second sentence,
         changing 90 to 60 and seeing old dates unchanged reads as a bug. */
      const name = stateNameForCode(this.form.stateCode);
      const impact = await this.impactFor(this.form.stateCode);
      if (impact && (impact.awaiting > 0 || impact.holding > 0)) {
        const lines = [`Apply the ${name} rule of ${this.form.reauthDays} days?`, ""];
        if (impact.awaiting > 0) {
          lines.push(
            `${impact.awaiting} active record${impact.awaiting === 1 ? "" : "s"} ` +
              `will be given a re-authorization deadline ${this.form.reauthDays} days from today.`
          );
        }
        if (impact.holding > 0) {
          lines.push(
            `${impact.holding} record${impact.holding === 1 ? "" : "s"} already ` +
              `${impact.holding === 1 ? "has" : "have"} a deadline and will keep it until re-authorized.`
          );
        }
        if (!window.confirm(lines.join("\n"))) return;
      }

      this.saving = true;
      this.error = "";
      try {
        const res = await saveDirectiveReauthPolicy({
          accountnumber: this.accountnumber,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          stateCode: this.form.stateCode,
          reauthDays: Number(this.form.reauthDays),
        });
        if (res && res.status === "Successful") {
          this.resetForm();
          await this.load();
        } else {
          this.error = (res && res.message) || "Could not save the rule.";
        }
      } catch (e) {
        console.error("[directive-policy] save failed", e);
        this.error = "Could not save the rule.";
      } finally {
        this.saving = false;
      }
    },
    async remove(policy) {
      const name = stateNameForCode(policy.state_code);
      /* Removal clears the deadlines this rule justified, atomically with the
         removal itself — nothing is left holding a deadline with no rule behind
         it. The count is what stops an accidental removal: a rule governing 47
         records reads very differently from one governing none. The rule stays
         deletable either way, because blocking it outright would make a rule
         permanently undeletable — re-authorization re-applies it, so the
         dependency could never be drained. */
      const impact = await this.impactFor(policy.state_code);
      const lines = [
        `Remove the rule for ${name}?`,
        "",
        `Directive Records governed by ${name} will no longer require re-authorization.`,
      ];
      if (impact && impact.holding > 0) {
        lines.push(
          "",
          `${impact.holding} active record${impact.holding === 1 ? "" : "s"} ` +
            `will have ${impact.holding === 1 ? "its" : "their"} current deadline cleared.`
        );
      }
      const ok = window.confirm(lines.join("\n"));
      if (!ok) return;
      this.saving = true;
      this.error = "";
      try {
        const res = await deleteDirectiveReauthPolicy({
          accountnumber: this.accountnumber,
          actorId: this.currentUserId,
          actorName: this.currentUserName,
          stateCode: policy.state_code,
        });
        if (res && res.status === "Successful") {
          if (this.editing && this.form.stateCode === policy.state_code) this.resetForm();
          await this.load();
        } else {
          this.error = (res && res.message) || "Could not remove the rule.";
        }
      } catch (e) {
        console.error("[directive-policy] delete failed", e);
        this.error = "Could not remove the rule.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.drp-page {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}
.drp-back {
  background: none;
  border: 0;
  color: #2a6fb5;
  cursor: pointer;
  padding: 0 0 8px;
  font-size: 0.95rem;
}
.drp-title {
  font-size: 1.6rem;
  margin: 0 0 6px;
}
.drp-lede {
  color: #5a6472;
  margin: 0 0 20px;
  max-width: 60ch;
}
.drp-denied,
.drp-error {
  background: #fdecea;
  border: 1px solid #f5c2bd;
  color: #8c231a;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.drp-card {
  background: #fff;
  border: 1px solid #e3e7ed;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 18px;
}
.drp-card-title {
  font-size: 1.05rem;
  margin: 0 0 14px;
}
.drp-form {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}
.drp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.drp-label {
  font-size: 0.85rem;
  color: #5a6472;
}
.drp-input {
  border: 1px solid #ccd3dc;
  border-radius: 5px;
  padding: 8px 10px;
  min-width: 220px;
  font-size: 0.95rem;
}
.drp-input-days {
  min-width: 90px;
  width: 90px;
}
.drp-days {
  display: flex;
  align-items: center;
  gap: 8px;
}
.drp-unit {
  color: #5a6472;
}
.drp-actions {
  display: flex;
  gap: 8px;
}
.drp-btn {
  border: 1px solid #ccd3dc;
  background: #f7f9fb;
  border-radius: 5px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.92rem;
}
.drp-btn[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}
.drp-btn-primary {
  background: #2a6fb5;
  border-color: #2a6fb5;
  color: #fff;
}
.drp-btn-sm {
  padding: 5px 10px;
  font-size: 0.85rem;
}
.drp-btn-danger {
  color: #8c231a;
  border-color: #e6b3ad;
}
.drp-table {
  width: 100%;
  border-collapse: collapse;
}
.drp-table th,
.drp-table td {
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid #eef1f5;
}
.drp-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #5a6472;
}
.drp-code {
  color: #8a94a3;
  font-size: 0.85rem;
}
.drp-row-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}
.drp-muted {
  color: #5a6472;
}
.drp-empty {
  color: #5a6472;
  background: #f7f9fb;
  border: 1px dashed #ccd3dc;
  border-radius: 6px;
  padding: 14px;
  margin: 0;
}
.drp-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
</style>
